/* ScrollTrigger saves the window scroll before every refresh and writes it back
   afterwards, and it auto-refreshes on load and resize. On a reload that races
   the browser restoring its own scroll position, and the page intermittently
   settles a few pixels down — with the header hidden, because a downward scroll
   is what hides it. A reload is ours to place, so start it at the top. Back and
   forward navigations still restore normally, and a deep link still wins. */
(function () {
  const entry =
    typeof performance !== "undefined" &&
    performance.getEntriesByType &&
    performance.getEntriesByType("navigation")[0];

  if (!entry || entry.type !== "reload" || window.location.hash) {
    return;
  }

  if ("scrollRestoration" in history) {
    history.scrollRestoration = "manual";
  }

  window.scrollTo(0, 0);
})();

gsap.registerPlugin(InertiaPlugin, ScrollTrigger, SplitText);

ScrollTrigger.config({
  ignoreMobileResize: true,
});

const lerp = (a, b, n) => (1 - n) * a + n * b;

class SmoothScaler {
  constructor(initialScale = 1, speed = 0.3) {
    this.currentScale = initialScale;
    this.targetScale = initialScale;
    this.speed = speed;
    this.animationFrame = null;
  }

  updateScale(targetScale) {
    this.targetScale = targetScale;

    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }

    this.animate();
  }

  animate() {
    this.currentScale = lerp(this.currentScale, this.targetScale, this.speed);

    document.documentElement.style.setProperty(
      "--cursor-scale",
      this.currentScale,
    );

    const delta = Math.abs(this.currentScale - this.targetScale);

    if (delta > 0.001) {
      this.animationFrame = requestAnimationFrame(() => this.animate());
    } else {
      this.currentScale = this.targetScale;
      document.documentElement.style.setProperty(
        "--cursor-scale",
        this.currentScale,
      );
      this.animationFrame = null;
    }
  }
}

var cursor = {
  target: { x: 0, y: 0 },
  current: { x: 0, y: 0 },
  speed: 0.2,
  cursorVisible: true,
  cursorEnlarged: false,
  cursorSpecial: false,
  cursorExternal: false,
  cursorOnPointer: false,
  pointerSelector: 'a, button, [role="button"], input, textarea, select, label',
  $dot: document.querySelector(".cursor-dot"),
  currentLensSection: null,
  lensOffset: { x: 0, y: 0 },

  raf: null,

  init: function () {
    if (!this.isViewportValid()) {
      return;
    }

    document.documentElement.style.setProperty("--cursor-x", "0px");
    document.documentElement.style.setProperty("--cursor-y", "0px");
    document.documentElement.style.setProperty("--cursor-scale", "1");
    document.documentElement.style.setProperty("--lens-cursor-x", "0px");
    document.documentElement.style.setProperty("--lens-cursor-y", "0px");

    this.setupEventListeners();
    this.animateDot();
  },

  scaler: new SmoothScaler(),

  isViewportValid: function () {
    return window.innerWidth >= 1199;
  },

  isPointerOrGrabState: function (x, y) {
    if (!this.isViewportValid()) {
      return false;
    }

    const element = document.elementFromPoint(x, y);
    if (!element) return false;

    if (element.closest && element.closest(this.pointerSelector)) {
      return true;
    }

    const computedStyle = window.getComputedStyle(element);
    const cursorValue = computedStyle.cursor.toLowerCase().trim();

    return (
      cursorValue === "pointer" ||
      cursorValue === "grab" ||
      cursorValue === "grabbing"
    );
  },

  setupEventListeners: function () {
    var self = this;

    document.querySelectorAll("a, button, input, label").forEach(function (el) {
      if (el.classList.contains("JScursorNoScale")) return;
      el.addEventListener("mouseover", function () {
        if (!self.isViewportValid()) return;
        self.cursorEnlarged = true;
        self.scaler.updateScale(1.8);
      });
      el.addEventListener("mouseout", function () {
        if (!self.isViewportValid()) return;
        self.cursorEnlarged = false;
        self.scaler.updateScale(1);
      });
    });

    document.querySelectorAll("[data-cursor='view']").forEach(function (el) {
      el.addEventListener("mouseover", function () {
        if (!self.isViewportValid()) return;
        self.cursorSpecial = true;
        self.$dot.classList.add("cursor-dot--view");
        self.updateCursorText("view");
      });
      el.addEventListener("mouseout", function () {
        if (!self.isViewportValid()) return;
        self.cursorSpecial = false;
        self.$dot.classList.remove("cursor-dot--view");
        self.updateCursorText("default");
      });
    });
    document.querySelectorAll("[data-cursor='drag']").forEach(function (el) {
      el.addEventListener("mouseover", function () {
        if (!self.isViewportValid()) return;
        self.cursorSpecial = true;
        self.$dot.classList.add("cursor-dot--view");
        self.updateCursorText("drag");
      });
      el.addEventListener("mouseout", function () {
        if (!self.isViewportValid()) return;
        self.cursorSpecial = false;
        self.$dot.classList.remove("cursor-dot--view");
        self.updateCursorText("default");
      });
    });

    document.querySelectorAll("[data-cursor='blur']").forEach(function (el) {
      el.addEventListener("mouseover", function () {
        if (!self.isViewportValid()) return;
        self.cursorSpecial = true;
        self.$dot.classList.add("cursor-dot--blur");
        self.updateCursorText("blur");
      });
      el.addEventListener("mouseout", function () {
        if (!self.isViewportValid()) return;
        self.cursorSpecial = false;
        self.$dot.classList.remove("cursor-dot--blur");
        self.updateCursorText("default");
      });
    });

    document.querySelectorAll("[data-cursor='donut']").forEach(function (el) {
      el.addEventListener("mouseover", function () {
        if (!self.isViewportValid()) return;
        self.cursorSpecial = true;
        self.$dot.classList.add("cursor-dot--donut");
        self.updateCursorText("donut");
      });
      el.addEventListener("mouseout", function () {
        if (!self.isViewportValid()) return;
        self.cursorSpecial = false;
        self.$dot.classList.remove("cursor-dot--donut");
        self.updateCursorText("default");
      });
    });

    document.querySelectorAll("[data-cursor='lens']").forEach(function (el) {
      const lensButton = el.querySelector(".l-btn--cursor-lens");

      const updateLensPosition = (clientX, clientY) => {
        if (!self.isViewportValid()) return;

        if (self.currentLensSection === el) {
          const rect = el.getBoundingClientRect();
          self.lensOffset.x = clientX - rect.left;
          self.lensOffset.y = clientY - rect.top;

          document.documentElement.style.setProperty(
            "--lens-cursor-x",
            `${self.lensOffset.x}px`,
          );
          document.documentElement.style.setProperty(
            "--lens-cursor-y",
            `${self.lensOffset.y}px`,
          );
        }
      };

      const handleScroll = () => {
        if (!self.isViewportValid()) return;

        if (self.currentLensSection === el && self.target.x && self.target.y) {
          const rect = el.getBoundingClientRect();
          const isInBounds =
            self.target.x >= rect.left &&
            self.target.x <= rect.right &&
            self.target.y >= rect.top &&
            self.target.y <= rect.bottom;

          if (isInBounds) {
            updateLensPosition(self.target.x, self.target.y);
          } else {
            self.cursorSpecial = false;
            self.cursorExternal = false;
            self.$dot.classList.remove("cursor-dot--lens");

            const parentCard = el.closest(
              ".podcast-card, .career-cta, .team-photos-marquee",
            );
            if (parentCard) {
              parentCard.classList.remove("lens-active");
            }

            if (lensButton) {
              lensButton.classList.remove("is-live");
            }
            self.currentLensSection = null;
            self.updateCursorText("default");

            window.removeEventListener("scroll", handleScroll);
          }
        }
      };

      el.addEventListener("mouseover", function (e) {
        if (!self.isViewportValid()) return;

        self.cursorSpecial = true;
        self.cursorExternal = true;
        self.$dot.classList.add("cursor-dot--lens");

        const parentCard = el.closest(
          ".podcast-card, .career-cta, .team-photos-marquee",
        );
        if (parentCard) {
          parentCard.classList.add("lens-active");
        }

        if (lensButton) {
          lensButton.classList.add("is-live");
        }
        self.updateCursorText("lens");

        self.currentLensSection = el;

        updateLensPosition(e.clientX, e.clientY);

        window.addEventListener("scroll", handleScroll, { passive: true });
      });

      el.addEventListener("mousemove", function (e) {
        if (!self.isViewportValid()) return;
        updateLensPosition(e.clientX, e.clientY);
      });

      el.addEventListener("mouseout", function () {
        if (!self.isViewportValid()) return;

        self.cursorSpecial = false;
        self.cursorExternal = false;
        self.$dot.classList.remove("cursor-dot--lens");

        const parentCard = el.closest(
          ".podcast-card, .career-cta, .team-photos-marquee",
        );
        if (parentCard) {
          parentCard.classList.remove("lens-active");
        }

        if (lensButton) {
          lensButton.classList.remove("is-live");
        }
        self.currentLensSection = null;
        self.updateCursorText("default");

        window.removeEventListener("scroll", handleScroll);
      });
    });

    document.addEventListener("mousedown", function () {
      if (!self.isViewportValid()) return;
      self.cursorEnlarged = true;
      self.scaler.updateScale(0.6);
    });

    document.addEventListener("mouseup", function () {
      if (!self.isViewportValid()) return;
      self.cursorEnlarged = false;
      self.scaler.updateScale(1);
    });

    document.addEventListener("mousemove", function (e) {
      if (!self.isViewportValid()) {
        return;
      }

      self.target.x = e.clientX;
      self.target.y = e.clientY;

      if (self.isPointerOrGrabState(e.clientX, e.clientY)) {
        if (!self.cursorSpecial) {
          if (!self.cursorOnPointer) {
            self.cursorOnPointer = true;
            if (self.$dot) self.$dot.classList.add("cursor-dot--pointer");
          }
        } else if (self.cursorOnPointer) {
          self.cursorOnPointer = false;
          if (self.$dot) self.$dot.classList.remove("cursor-dot--pointer");
        }
        if (!self.cursorVisible) {
          self.cursorVisible = true;
          document.documentElement.style.setProperty("--cursor-opacity", "1");
        }
        if (!self.raf) {
          self.raf = requestAnimationFrame(self.animateDot.bind(self));
        }
        return;
      }

      if (self.cursorOnPointer) {
        self.cursorOnPointer = false;
        if (self.$dot) self.$dot.classList.remove("cursor-dot--pointer");
      }

      let isOverIframe = false;

      const elementUnderCursor = document.elementFromPoint(
        e.clientX,
        e.clientY,
      );
      if (elementUnderCursor) {
        isOverIframe =
          elementUnderCursor.tagName === "IFRAME" ||
          elementUnderCursor.closest("iframe") !== null;
      }

      if (!isOverIframe) {
        const iframes = document.querySelectorAll("iframe");
        for (let i = 0; i < iframes.length; i++) {
          const rect = iframes[i].getBoundingClientRect();
          if (
            e.clientX >= rect.left &&
            e.clientX <= rect.right &&
            e.clientY >= rect.top &&
            e.clientY <= rect.bottom
          ) {
            isOverIframe = true;
            break;
          }
        }
      }

      if (isOverIframe) {
        self.cursorVisible = false;
        document.documentElement.style.setProperty("--cursor-opacity", "0");
        if (self.raf) {
          cancelAnimationFrame(self.raf);
          self.raf = null;
        }
      } else {
        if (!self.cursorVisible) {
          self.current.x = self.target.x;
          self.current.y = self.target.y;
        }
        self.cursorVisible = true;
        document.documentElement.style.setProperty("--cursor-opacity", "1");
      }

      if (!self.raf) {
        self.raf = requestAnimationFrame(self.animateDot.bind(self));
      }
    });

    document.addEventListener("mouseenter", function () {
      if (!self.isViewportValid()) return;
      self.cursorVisible = true;
      document.documentElement.style.setProperty("--cursor-opacity", "1");
    });

    document.addEventListener("mouseleave", function () {
      if (!self.isViewportValid()) return;
      self.cursorVisible = false;
      document.documentElement.style.setProperty("--cursor-opacity", "0");
      if (self.cursorOnPointer) {
        self.cursorOnPointer = false;
        if (self.$dot) self.$dot.classList.remove("cursor-dot--pointer");
      }
    });
  },

  animateDot: function () {
    if (!this.isViewportValid()) {
      this.raf = null;
      return;
    }

    if (!this.cursorVisible && !this.cursorOnPointer) {
      this.raf = null;
      return;
    }

    this.current.x = lerp(this.current.x, this.target.x, this.speed);
    this.current.y = lerp(this.current.y, this.target.y, this.speed);

    document.documentElement.style.setProperty(
      "--cursor-x",
      `${this.current.x}px`,
    );
    document.documentElement.style.setProperty(
      "--cursor-y",
      `${this.current.y}px`,
    );

    this.raf = requestAnimationFrame(this.animateDot.bind(this));
  },

  updateCursorText: function (cursorType) {
    const textMap = {
      default: "view",
      view: "view",
      drag: "drag",
      blur: "blur",
      donut: "donut",
      lens: "lens",
    };

    const text = textMap[cursorType] || "view";

    const dotSpan = this.$dot && this.$dot.querySelector("span");
    if (dotSpan) dotSpan.textContent = text;

    if (!this.$ring) {
      this.$ring = document.querySelector(".cursor-ring");
    }
    const ringSpan = this.$ring && this.$ring.querySelector("span");
    if (ringSpan) ringSpan.textContent = text;
  },
};

let lastScrollTop = 0;
/* The header is translated off-screen by default, so it only appears once this
   adds `header--show`. Anywhere near the top it is always shown: on a refresh
   the browser restores the previous scroll offset, and counting that as a
   downward scroll used to leave the nav hidden until the next upward scroll. */
const HEADER_PEEK_TOP = 120;
function peekingHeader() {
  let scrollTop = window.scrollY;

  if (scrollTop > HEADER_PEEK_TOP && scrollTop > lastScrollTop) {
    $(".header").removeClass("header--show");
  } else {
    $(".header").addClass("header--show");
  }

  lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
}

function handleHeaderColor() {
  const header = document.querySelector(".wrg.header");

  if (!header) {
    return;
  }

  function checkOverlap() {
    const lightSections = document.querySelectorAll(
      ".section--white, .section--grey"
    );

    if (lightSections.length === 0) {
      return;
    }

    let isOverlapping = false;
    const headerRect = header.getBoundingClientRect();

    lightSections.forEach((section) => {
      const sectionRect = section.getBoundingClientRect();

      if (
        sectionRect.top < headerRect.bottom / 2 &&
        sectionRect.bottom > headerRect.bottom / 2
      ) {
        isOverlapping = true;
      }
    });

    if (isOverlapping) {
      header.classList.add("header--white");
    } else {
      header.classList.remove("header--white");
    }
  }

  checkOverlap();

  return checkOverlap;
}

const headerColorHandler = handleHeaderColor();

function setDefaultPointer() {
  $("a").each(function () {
    var $link = $(this);
    if ($link.hasClass("jsLoadMore")) {
      return;
    }
    var href = $link.attr("href");
    if (!href || href === "" || href === "#" || href === "#!") {
      return; // No-op for empty or placeholder hrefs
    }
  });
}

function setupCardPrefetching() {
  const prefetchedUrls = new Set();

  const cardLinks = document.querySelectorAll(".project-card");

  cardLinks.forEach((card) => {
    if (card.tagName !== "A" || !card.href) return;

    card.addEventListener("mouseenter", () => {
      const url = card.href;

      if (prefetchedUrls.has(url)) return;

      prefetchedUrls.add(url);

      const prefetchLink = document.createElement("link");
      prefetchLink.rel = "prefetch";
      prefetchLink.href = url;
      document.head.appendChild(prefetchLink);

      fetch(url)
        .then((response) => response.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");

          const cssLinks = doc.querySelectorAll('link[rel="stylesheet"]');
          cssLinks.forEach((cssLink) => {
            if (cssLink.href && !prefetchedUrls.has(cssLink.href)) {
              const cssPrefetch = document.createElement("link");
              cssPrefetch.rel = "prefetch";
              cssPrefetch.href = cssLink.href;
              document.head.appendChild(cssPrefetch);
              prefetchedUrls.add(cssLink.href);
            }
          });

          const scripts = doc.querySelectorAll("script[src]");
          scripts.forEach((script) => {
            if (script.src && !prefetchedUrls.has(script.src)) {
              const jsPrefetch = document.createElement("link");
              jsPrefetch.rel = "prefetch";
              jsPrefetch.href = script.src;
              document.head.appendChild(jsPrefetch);
              prefetchedUrls.add(script.src);
            }
          });
        })
        .catch(() => {});
    });
  });
}

function asciiHover() {
  if (document.querySelector(".work-grid")) {
    const grid = document.querySelector(".work-grid");
    const projectCards = document.querySelectorAll(".work-grid .project-card");

    projectCards.forEach((card) => {
      card.addEventListener("mouseenter", () => {
        if (window.innerWidth > 1199) {
          const projectColor =
            getComputedStyle(card).getPropertyValue("--project-color");
          grid.style.setProperty("--project-color", projectColor);
        }
      });

      card.addEventListener("mouseleave", () => {
        if (window.innerWidth > 1199) {
          grid.style.removeProperty("--project-color");
        }
      });
    });

    grid.addEventListener("mousemove", (e) => {
      if (window.innerWidth > 1199) {
        const rect = grid.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        grid.style.setProperty("--ascii-mask-x", `${x}%`);
        grid.style.setProperty("--ascii-mask-y", `${y}%`);
      }
    });
  }
}

function initGlowingInteractiveDotsGrid() {
  let isLargeDevice = window.innerWidth >= 700;

  if (!isLargeDevice) {
    return;
  }

  const containers = document.querySelectorAll("[data-dots-container-init]");

  let mouseMoveTimeout;
  const throttledMouseMove = (e) => {
    const containerUnderMouse = Array.from(containers).find((container) => {
      const rect = container.getBoundingClientRect();
      return (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      );
    });

    if (containerUnderMouse && containerUnderMouse._handleMouseMove) {
      clearTimeout(mouseMoveTimeout);
      mouseMoveTimeout = setTimeout(() => {
        containerUnderMouse._handleMouseMove(e);
      }, 8);
    }
  };

  const handleClickGlobal = (e) => {
    const containerUnderClick = Array.from(containers).find((container) => {
      const rect = container.getBoundingClientRect();
      return (
        e.clientX >= rect.left &&
        e.clientX <= rect.right &&
        e.clientY >= rect.top &&
        e.clientY <= rect.bottom
      );
    });

    if (containerUnderClick && containerUnderClick._handleClick) {
      containerUnderClick._handleClick(e);
    }
  };

  window.addEventListener("mousemove", throttledMouseMove);
  window.addEventListener("click", handleClickGlobal);

  containers.forEach((container) => {
    const baseColor = container.dataset.dotsBaseColor || "#363636";
    const activeColor = container.dataset.dotsActiveColor || "#ffffff";
    const hoverEnabled =
      container.dataset.dotsHover === undefined ||
      container.dataset.dotsHover === "" ||
      /^(1|true|yes|on)$/i.test(container.dataset.dotsHover);

    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : null;
    };

    const baseRgb = hexToRgb(baseColor);
    const activeRgb = hexToRgb(activeColor);

    const colors = {
      base: baseColor,
      active: activeColor,
      baseRgb: baseRgb || { r: 54, g: 54, b: 54 },
      activeRgb: activeRgb || { r: 255, g: 255, b: 255 },
    };
    const threshold = 160; // Proximity
    const speedThreshold = 10;
    const shockRadius = 260; // Shock Radius
    const shockPower = 4; // Shock Strength
    const maxSpeed = 5000;

    let canvas = null;
    let ctx = null;
    let animationId = null;

    let dots = [];
    let dotCenters = [];
    let mouseX = 0;
    let mouseY = 0;
    let lastMouseX = 0;
    let lastMouseY = 0;
    let lastTime = 0;
    let vx = 0;
    let vy = 0;
    let speed = 0;
    let containerRect = null;

    const maxDots = 10000; // Increased since Canvas can handle more
    const dotSize = 4; // Match CSS font-size
    const gapSize = 15; // Match CSS gap

    function createCanvas() {
      canvas = document.createElement("canvas");
      canvas.style.cssText = `
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: auto;
          z-index: 2;
          background: transparent;
        `;

      container.appendChild(canvas);

      ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
    }

    function buildGrid() {
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }

      if (!canvas) createCanvas();

      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      if (rect.width === 0 || rect.height === 0) {
        setTimeout(() => buildGrid(), 100);
        return;
      }

      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
      ctx.scale(dpr, dpr);

      containerRect = rect;

      const contW = rect.width;
      const contH = rect.height;
      const cols = Math.floor((contW + gapSize) / (dotSize + gapSize));
      const rows = Math.floor((contH + gapSize) / (dotSize + gapSize));
      const total = Math.min(cols * rows, maxDots);

      dots = [];
      dotCenters = [];

      /* This below commented out code is the original organic pattern code that was used to create the gaps in the dots grid.
         it was replaced with const createOrganicPattern = () => false; to remove the gaps in the dots grid.
      */
      // const createOrganicPattern = (row, col, totalRows, totalCols) => {
      //   const centerX = totalCols / 2;
      //   const centerY = totalRows / 2;

      //   const holes = [
      //     {
      //       x: centerX,
      //       y: centerY,
      //       radius: Math.min(totalCols, totalRows) * 0.15,
      //       shape: "circle",
      //     },
      //     {
      //       x: centerX - totalCols * 0.3,
      //       y: centerY - totalRows * 0.1,
      //       radius: Math.min(totalCols, totalRows) * 0.12,
      //       shape: "organic",
      //     },
      //     {
      //       x: centerX + totalCols * 0.25,
      //       y: centerY + totalRows * 0.15,
      //       radius: Math.min(totalCols, totalRows) * 0.08,
      //       shape: "circle",
      //     },
      //     {
      //       x: centerX - totalCols * 0.4,
      //       y: centerY - totalRows * 0.3,
      //       radius: Math.min(totalCols, totalRows) * 0.1,
      //       shape: "organic",
      //     },
      //     {
      //       x: centerX + totalCols * 0.35,
      //       y: centerY + totalRows * 0.25,
      //       radius: Math.min(totalCols, totalRows) * 0.09,
      //       shape: "organic",
      //     },
      //   ];

      //   for (const hole of holes) {
      //     const distToHole = Math.sqrt(
      //       Math.pow(col - hole.x, 2) + Math.pow(row - hole.y, 2),
      //     );

      //     if (hole.shape === "circle") {
      //       if (distToHole < hole.radius) return true;
      //     } else if (hole.shape === "organic") {
      //       const noise = Math.sin(col * 0.3) * Math.cos(row * 0.2) * 0.3;
      //       const organicRadius = hole.radius + noise;
      //       if (distToHole < organicRadius) return true;
      //     }
      //   }

      //   const randomSparse = Math.random() < 0.02;
      //   if (randomSparse) return true;

      //   const edgeDistance = Math.min(
      //     Math.min(col, totalCols - col),
      //     Math.min(row, totalRows - row),
      //   );
      //   if (edgeDistance < 2 && Math.random() < 0.3) return true;

      //   return false;
      // };
      const createOrganicPattern = () => false;

      for (let i = 0; i < total; i++) {
        const row = Math.floor(i / cols);
        const col = i % cols;
        const isHole = createOrganicPattern(row, col, rows, cols);

        if (!isHole) {
          const x = col * (dotSize + gapSize) + dotSize / 2;
          const y = row * (dotSize + gapSize) + dotSize / 2;

          const dot = {
            x: x,
            y: y,
            originalX: x,
            originalY: y,
            size: dotSize,
            color: colors.baseRgb,
            opacity: 1.0,
            scale: 1,
            inertiaX: 0,
            inertiaY: 0,
            inertiaApplied: false,
            targetColor: colors.baseRgb,
            targetOpacity: 1.0,
            targetScale: 1,
            targetInertiaX: 0,
            targetInertiaY: 0,
          };

          dots.push(dot);
          dotCenters.push({
            dot: dot,
            x: x + rect.left + window.scrollX,
            y: y + rect.top + window.scrollY,
          });
        }
      }

      animate();
    }

    function animate() {
      if (!ctx || !canvas) return;

      animationId = requestAnimationFrame(animate);

      ctx.save();
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      dots.forEach((dot) => {
        dot.x = lerp(dot.x, dot.originalX + dot.inertiaX, 0.1);
        dot.y = lerp(dot.y, dot.originalY + dot.inertiaY, 0.1);
        dot.color = {
          r: Math.round(lerp(dot.color.r, dot.targetColor.r, 0.1)),
          g: Math.round(lerp(dot.color.g, dot.targetColor.g, 0.1)),
          b: Math.round(lerp(dot.color.b, dot.targetColor.b, 0.1)),
        };
        dot.opacity = lerp(dot.opacity, dot.targetOpacity, 0.05);
        dot.scale = lerp(dot.scale, dot.targetScale, 0.1);
        dot.inertiaX = lerp(dot.inertiaX, dot.targetInertiaX, 0.05);
        dot.inertiaY = lerp(dot.inertiaY, dot.targetInertiaY, 0.05);

        ctx.save();
        ctx.globalAlpha = dot.opacity;
        ctx.fillStyle = `rgb(${dot.color.r}, ${dot.color.g}, ${dot.color.b})`;
        ctx.translate(dot.x, dot.y);
        ctx.beginPath();
        ctx.arc(0, 0, dot.size / 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      ctx.restore();
    }

    function lerp(a, b, t) {
      return a + (b - a) * t;
    }

    if (hoverEnabled) {
      container._handleMouseMove = function handleMouseMove(e) {
        if (!containerRect) return;

        const now = performance.now();
        const dt = now - lastTime || 16;

        const rect = canvas.getBoundingClientRect();
        mouseX = e.clientX - rect.left;
        mouseY = e.clientY - rect.top;

        const dx = mouseX - lastMouseX;
        const dy = mouseY - lastMouseY;
        vx = (dx / dt) * 1000;
        vy = (dy / dt) * 1000;
        speed = Math.hypot(vx, vy);

        if (speed > maxSpeed) {
          const scale = maxSpeed / speed;
          vx *= scale;
          vy *= scale;
          speed = maxSpeed;
        }

        lastTime = now;
        lastMouseX = mouseX;
        lastMouseY = mouseY;

        dots.forEach((dot) => {
          const dist = Math.hypot(dot.x - mouseX, dot.y - mouseY);

          if (dist < threshold) {
            const t = Math.max(0, 1 - dist / threshold);
            const easedT = t * t * (3 - 2 * t); // Smoothstep easing

            dot.targetColor = {
              r: Math.round(lerp(colors.baseRgb.r, colors.activeRgb.r, easedT)),
              g: Math.round(lerp(colors.baseRgb.g, colors.activeRgb.g, easedT)),
              b: Math.round(lerp(colors.baseRgb.b, colors.activeRgb.b, easedT)),
            };
            dot.targetScale = lerp(1, 1.2, easedT);

            if (speed > speedThreshold && !dot.inertiaApplied) {
              dot.inertiaApplied = true;
              const pushX = (dot.x - mouseX) * 0.1 + vx * 0.003;
              const pushY = (dot.y - mouseY) * 0.1 + vy * 0.003;

              dot.targetInertiaX = pushX;
              dot.targetInertiaY = pushY;

              setTimeout(() => {
                dot.inertiaApplied = false;
                dot.targetInertiaX = 0;
                dot.targetInertiaY = 0;
              }, 100);
            }
          } else {
            dot.targetColor = colors.baseRgb;
            dot.targetOpacity = 1.0;
            dot.targetScale = 1;
          }
        });
      };
    }

    container._handleClick = function handleClick(e) {
      if (!containerRect) return;

      const clickX = e.pageX - containerRect.left;
      const clickY = e.pageY - containerRect.top;

      dots.forEach((dot) => {
        const dist = Math.hypot(dot.x - clickX, dot.y - clickY);
        if (dist < shockRadius && !dot.inertiaApplied) {
          dot.inertiaApplied = true;
          const falloff = Math.max(0, 1 - dist / shockRadius);
          const pushX = (dot.x - clickX) * shockPower * falloff;
          const pushY = (dot.y - clickY) * shockPower * falloff;

          dot.targetInertiaX = pushX;
          dot.targetInertiaY = pushY;

          setTimeout(() => {
            dot.inertiaApplied = false;
            dot.targetInertiaX = 0;
            dot.targetInertiaY = 0;
          }, 2000);
        }
      });
    };

    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        containerRect = container.getBoundingClientRect();
        buildGrid();
      }, 100);
    });

    buildGrid();

    const cleanup = () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", buildGrid);
      delete container._handleMouseMove;
      delete container._handleClick;
      if (canvas && canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    };

    container._cleanupDotsGrid = cleanup;
  });
}

function initHeaderNavAnimation() {
  if (window.innerWidth >= 1280) {
    const headerNavLinks = document.querySelectorAll(".header__nav a");
    headerNavLinks.forEach((a) => {
      if (
        a.childNodes.length === 1 &&
        a.childNodes[0].nodeType === Node.TEXT_NODE &&
        a.textContent.trim().length > 0
      ) {
        const text = a.textContent;
        const wordSpan = document.createElement("span");
        wordSpan.className = "split-word";
        for (const char of text) {
          const charSpan = document.createElement("span");
          charSpan.className = "split-char";
          charSpan.textContent = char;
          wordSpan.appendChild(charSpan);
        }
        a.textContent = "";
        a.appendChild(wordSpan);
      }
    });

    setTimeout(() => {
      animateHeaderNavWords();
    }, 100);
  }
}

function animateHeaderNavWords() {
  const splitWords = document.querySelectorAll(".header__nav .split-word");
  let maxAnimationDuration = 0;

  if (splitWords.length > 0) {
    splitWords.forEach((word) => {
      const chars = word.querySelectorAll(".split-char");
      if (chars.length > 0) {
        const wordDuration = (chars.length - 1) * 100 + 100;
        maxAnimationDuration = Math.max(maxAnimationDuration, wordDuration);
      }
    });

    splitWords.forEach((word) => {
      const chars = word.querySelectorAll(".split-char");
      const dropdownToggle = word.closest(".header__dropdown-toggle");

      chars.forEach((char, i) => {
        setTimeout(() => {
          char.style.opacity = "1";
          if (i > 0) {
            chars[i - 1].classList.add("split-char-caret-hide");
          }
          if (i === chars.length - 1) {
            /* The chevron belongs to the word that just finished typing, so it
               comes in with that last character rather than a beat behind it. */
            if (dropdownToggle) {
              dropdownToggle.classList.add("is-chevron-visible");
            }
            setTimeout(() => {
              char.classList.add("split-char-caret-hide");
            }, 100);
          }
        }, i * 100);
      });
    });

    const headerIcon = document.querySelector(".header__icon.is-desktop");
    if (headerIcon && maxAnimationDuration > 0) {
      headerIcon.classList.add("is-visible");
    }
  } else {
    const headerIcon = document.querySelector(".header__icon.is-desktop");
    if (headerIcon) {
      headerIcon.classList.add("is-visible");
    }
    /* Nothing was split, so no last character will ever arrive to reveal the
       chevrons. Without this they stay at opacity 0 for the whole visit. */
    document
      .querySelectorAll(".header__dropdown-toggle")
      .forEach((toggle) => toggle.classList.add("is-chevron-visible"));
  }
}

function initScrollReveal() {
  const defaultOptions = {
    duration: 1,
    ease: "power2.out",
    y: 50,
    opacity: 0,
    stagger: 0.1,
    delay: 0,
  };

  function parseDataAttributes(element) {
    const options = { ...defaultOptions };

    if (element.dataset.revealDuration) {
      options.duration = parseFloat(element.dataset.revealDuration);
    }

    if (element.dataset.revealDelay) {
      options.delay = parseFloat(element.dataset.revealDelay);
    }

    if (element.dataset.revealY) {
      options.y = parseFloat(element.dataset.revealY);
    }

    if (element.dataset.revealOpacity) {
      options.opacity = parseFloat(element.dataset.revealOpacity);
    }

    if (element.dataset.revealStagger) {
      options.stagger = parseFloat(element.dataset.revealStagger);
    }

    if (element.dataset.revealEase) {
      options.ease = element.dataset.revealEase;
    }

    if (element.dataset.revealImmediate !== undefined) {
      options.immediate = true;
    }

    if (element.dataset.revealStart !== undefined) {
      const startPercent = parseFloat(element.dataset.revealStart);
      if (!isNaN(startPercent)) {
        options.triggerStart = `top ${startPercent}%`;
      }
    }

    return options;
  }

  function isElementPastTrigger(element, triggerStart = "top 55%") {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const scrollY = window.scrollY || window.pageYOffset;
    const elementTop = rect.top + scrollY;

    const triggerPercent = parseFloat(triggerStart.split(" ")[1]) / 100 || 0.55;
    const triggerPoint = scrollY + windowHeight * triggerPercent;

    return elementTop < triggerPoint;
  }

  /** Let CSS `:hover` transforms work after stagger reveal (same as `data-reveal-trigger` + `l-btn--estimator`). */
  function clearEstimatorButtonTransformFromStagger(children) {
    if (typeof gsap === "undefined" || !children || children.length === 0) {
      return;
    }
    Array.prototype.forEach.call(children, (child) => {
      if (child.classList.contains("l-btn--estimator")) {
        gsap.set(child, { clearProps: "transform" });
      }
    });
  }

  function setInitialState(element, triggerStart = "top 55%") {
    const options = parseDataAttributes(element);

    if (isElementPastTrigger(element, triggerStart)) {
      return;
    }

    const yValue = typeof options.y === "number" ? `${options.y}px` : options.y;
    gsap.set(element, {
      y: yValue,
      opacity: options.opacity,
      visibility: "hidden", // Ensure element is completely hidden
      force3D: true, // Enable hardware acceleration
    });
  }

  function ensureMobileVisibility() {
    if (window.innerWidth >= 1200) {
      return; // Only run on mobile
    }

    const revealElements = document.querySelectorAll("[data-reveal]");

    revealElements.forEach((element) => {
      gsap.set(element, {
        y: "0px",
        opacity: 1,
        visibility: "visible",
        clearProps: "all",
      });
    });
  }

  function createScrollTrigger(element) {
    const options = parseDataAttributes(element);

    if (options.immediate) {
      gsap.to(element, {
        y: "0px",
        opacity: 1, // Match the CSS final opacity
        visibility: "visible",
        duration: options.duration,
        ease: options.ease,
        force3D: true, // Enable hardware acceleration to prevent jerking
        onComplete: () => {
          element.classList.add("is-revealed");
        },
      });
    } else {
      const triggerStart = options.triggerStart || "top 75%";
      const isAlreadyVisible = isElementPastTrigger(element, triggerStart);

      if (isAlreadyVisible) {
        gsap.set(element, {
          y: "0px",
          opacity: 1,
          visibility: "visible",
          force3D: true,
        });
        element.classList.add("is-revealed");
        return; // Don't create ScrollTrigger for already visible elements
      }

      const animation = gsap.to(element, {
        y: "0px",
        opacity: 1,
        visibility: "visible", // Make element visible when animation plays
        duration: 1,
        ease: options.ease,

        force3D: true, // Enable hardware acceleration to prevent jerking
        onComplete: () => {
          element.classList.add("is-revealed");
          if (animation.scrollTrigger) {
            animation.scrollTrigger.disable();
          }
        },
        scrollTrigger: {
          trigger: element,
          start: "top 90%",
          end: "bottom 15%",
          toggleActions: "play none none none",
          once: true,
          markers: false, // Set to true for debugging
          onRefresh: function (self) {
            if (self.progress > 0) {
              const computedStyle = window.getComputedStyle(element);
              if (
                computedStyle.visibility === "hidden" ||
                computedStyle.opacity === "0"
              ) {
                gsap.set(element, {
                  y: "0px",
                  opacity: 1,
                  visibility: "visible",
                  force3D: true,
                });
              }
            }
          },
        },
      });
    }
  }

  function createStaggerAnimation(element) {
    if (element.classList.contains("is-revealed")) {
      const children = element.children;
      if (children.length > 0) {
        gsap.set(children, {
          opacity: 1,
          y: "0px",
          visibility: "visible",
        });
        clearEstimatorButtonTransformFromStagger(children);
      }
      return;
    } else {
      element.setAttribute("data-reveal-initialized", "true");
    }

    const options = parseDataAttributes(element);
    const children = element.children;

    if (children.length === 0) return;

    const ensureYWithUnits = (y) => {
      if (typeof y === "number") {
        return `${y}px`;
      }
      if (typeof y === "string" && !y.match(/px|%|em|rem|vh|vw$/)) {
        return `${y}px`;
      }
      return y || "0px";
    };

    gsap.set(element, {
      opacity: 1,
      visibility: "visible",
      y: "0px",
      force3D: true,
    });

    if (options.immediate) {
      gsap.set(children, {
        y: ensureYWithUnits(options.y),
        opacity: 0.01,
        visibility: "visible", // Keep visible so counters can read innerText
        force3D: true, // Enable hardware acceleration to prevent jerking
      });

      gsap.to(children, {
        y: "0px",
        opacity: 1,
        visibility: "visible",
        duration: 1,
        ease: options.ease,
        stagger: options.stagger,
        force3D: true, // Enable hardware acceleration to prevent jerking
        onComplete: () => {
          clearEstimatorButtonTransformFromStagger(children);
        },
      });
    } else {
      const triggerStart = options.triggerStart || "top 85%";
      const isAlreadyVisible = isElementPastTrigger(element, triggerStart);

      if (isAlreadyVisible) {
        gsap.set(children, {
          y: ensureYWithUnits(options.y),
          opacity: 0.01,
          visibility: "visible",
          force3D: true,
        });
        gsap.to(children, {
          y: "0px",
          opacity: 1,
          visibility: "visible",
          duration: 1,
          ease: options.ease,
          stagger: options.stagger,
          force3D: true,
          onComplete: () => {
            gsap.set(children, {
              opacity: 1,
              y: "0px",
            });
            clearEstimatorButtonTransformFromStagger(children);
            element.classList.add("is-revealed");
          },
        });
        const totalDuration =
          options.duration +
          options.delay +
          options.stagger * (children.length - 1);
        setTimeout(() => {
          if (!element.classList.contains("is-revealed")) {
            gsap.set(children, {
              opacity: 1,
              y: "0px",
            });
            clearEstimatorButtonTransformFromStagger(children);
            element.classList.add("is-revealed");
          }
        }, totalDuration); // Add 100ms buffer
        return; // Don't create ScrollTrigger for already visible elements
      }
      gsap.set(children, {
        y: ensureYWithUnits(options.y),
        opacity: 0.01,
        visibility: "visible", // Keep visible so counters can read innerText
        force3D: true, // Enable hardware acceleration to prevent jerking
      });

      const animation = gsap.to(children, {
        y: "0px",
        opacity: 1,
        visibility: "visible", // Make children visible when animation plays
        duration: 1,
        ease: options.ease,

        stagger: options.stagger,
        force3D: true, // Enable hardware acceleration to prevent jerking
        onComplete: () => {
          clearEstimatorButtonTransformFromStagger(children);
          element.classList.add("is-revealed");
          if (animation.scrollTrigger) {
            animation.scrollTrigger.disable();
          }
        },
        scrollTrigger: {
          trigger: element,
          start: triggerStart,
          end: "bottom 15%",
          toggleActions: "play none none none",
          once: true,
          markers: false, // Set to true for debugging
          onLeaveBack: function () {},
          onRefresh: function (self) {
            if (self.progress > 0 || self.isActive) {
              if (!element.classList.contains("is-revealed")) {
                gsap.set(children, {
                  y: ensureYWithUnits(options.y),
                  opacity: 0.01,
                  visibility: "visible",
                  force3D: true,
                });
                gsap.to(children, {
                  y: "0px",
                  opacity: 1,
                  visibility: "visible",
                  duration: 1,
                  ease: options.ease,

                  stagger: options.stagger,
                  force3D: true,
                  onComplete: () => {
                    clearEstimatorButtonTransformFromStagger(children);
                    element.classList.add("is-revealed");
                  },
                });
              }
            }
          },
        },
      });
    }
  }

  function initElements() {
    if (window.innerWidth < 1199) {
      ensureMobileVisibility();
      return;
    }

    const allRevealElements = document.querySelectorAll("[data-reveal]");

    const revealElements = Array.from(allRevealElements).filter(
      (element) =>
        !element.classList.contains("is-revealed") ||
        !element.hasAttribute("data-reveal-initialized"),
    );

    revealElements.forEach((element) => {
      if (element.dataset.revealStagger !== undefined) {
        createStaggerAnimation(element);
      } else {
        const options = parseDataAttributes(element);
        const triggerStart = options.triggerStart || "top 55%";

        if (options.immediate) {
          const ensureYWithUnits = (y) => {
            if (typeof y === "number") {
              return `${y}px`;
            }
            if (typeof y === "string" && !y.match(/px|%|em|rem|vh|vw$/)) {
              return `${y}px`;
            }
            return y || "0px";
          };
          gsap.set(element, {
            y: ensureYWithUnits(options.y),
            opacity: 0,
            visibility: "hidden",
            force3D: true,
          });
        } else {
          setInitialState(element, triggerStart);
        }

        createScrollTrigger(element);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initElements);
  } else {
    initElements();
  }

  window.initScrollReveal = initElements;
}

function initScrollRevealTrigger() {
  function parseDataAttributes(element) {
    const options = {
      animationDuration: 1, // Default animation duration in seconds
      delay: 0, // Delay before triggering in milliseconds
      triggerStart: "top 95%", // Default trigger start position
    };

    if (element.dataset.revealDuration) {
      options.animationDuration = parseFloat(element.dataset.revealDuration);
    }

    if (element.dataset.revealDelay) {
      options.delay = parseFloat(element.dataset.revealDelay) * 1000; // Convert to milliseconds
    }

    const triggerArea = element.getAttribute("trigger-area");
    if (triggerArea) {
      switch (triggerArea.toLowerCase()) {
        case "center":
          options.triggerStart = "center center";
          break;
        case "top":
          options.triggerStart = "top top";
          break;
        case "bottom":
          options.triggerStart = "bottom bottom";
          break;
        default:
          "top 85%";
          break;
      }
    }

    return options;
  }

  function initElements() {
    if (typeof ScrollTrigger === "undefined") {
      console.warn(
        "ScrollTrigger is not available. data-reveal-trigger will not work.",
      );
      return;
    }

    const allTriggerElements = document.querySelectorAll(
      "[data-reveal-trigger]",
    );

    // Only run reveal animation on viewports >= 1199px; on smaller devices show content immediately
    if (window.innerWidth < 1199) {
      allTriggerElements.forEach((el) => {
        gsap.set(el, {
          opacity: 1,
          y: 0,
          visibility: "visible",
          clearProps: "transform",
        });
      });
      return;
    }

    const triggerElements = Array.from(allTriggerElements).filter(
      (element) =>
        !element.classList.contains("is-revealed") ||
        !element.hasAttribute("data-reveal-trigger-initialized"),
    );

    if (triggerElements.length === 0) {
      return;
    }

    triggerElements.forEach((element) => {
      const options = parseDataAttributes(element);

      function isElementPastTrigger(element, triggerStart = "top 85%") {
        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const scrollY = window.scrollY || window.pageYOffset;
        const elementTop = rect.top + scrollY;

        const triggerPercent =
          parseFloat(triggerStart.split(" ")[1]) / 100 || 0.85;
        const triggerPoint = scrollY + windowHeight * triggerPercent;

        return elementTop < triggerPoint;
      }

      gsap.set(element, {
        opacity: 0.01,
        y: "50px", // Start 50px below for smooth upward translation
        visibility: "visible", // Keep visible so content can be read (e.g., counters)
        force3D: true, // Force GPU acceleration to prevent jerking
        willChange: "transform, opacity", // Enable hardware acceleration
        transformOrigin: "center center", // Ensure consistent transform origin
      });

      let scrollTriggerInstance = null;

      const animateElement = () => {
        gsap.to(element, {
          opacity: 1,
          y: "0px",
          duration: options.animationDuration,
          delay: options.delay / 1000,

          ease: "power2.out", // Smooth easing for natural motion
          force3D: true,
          onStart: () => {
            element.style.willChange = "transform, opacity";
            if (
              element.classList.contains("l-btn") ||
              element.classList.contains("l-btn--estimator") ||
              element.classList.contains("estimator-banner__card")
            ) {
              element.style.transition = "none";
            }
          },
          onComplete: () => {
            element.style.willChange = "auto";

            const event = new CustomEvent("revealTriggered", {
              detail: { element },
              bubbles: true,
            });
            element.dispatchEvent(event);

            element.classList.add("is-revealed");

            if (
              element.classList.contains("l-btn") ||
              element.classList.contains("l-btn--estimator") ||
              element.classList.contains("estimator-banner__card")
            ) {
              element.style.removeProperty("transition");
            }
            if (element.classList.contains("l-btn--estimator")) {
              gsap.set(element, { clearProps: "transform" });
            }
            if (element.classList.contains("estimator-banner__card")) {
              gsap.set(element, { clearProps: "transform" });
            }

            if (scrollTriggerInstance) {
              scrollTriggerInstance.kill();
              scrollTriggerInstance = null;
            }
          },
        });
      };

      const isAlreadyVisible = isElementPastTrigger(
        element,
        options.triggerStart,
      );

      if (isAlreadyVisible) {
        animateElement();
      } else {
        scrollTriggerInstance = ScrollTrigger.create({
          trigger: element,
          start: options.triggerStart,
          end: "bottom 15%",
          once: true, // Only trigger once when section becomes visible
          markers: false, // Set to true for debugging
          anticipatePin: 1, // Optimize for smoother scrolling
          invalidateOnRefresh: true, // Prevent unnecessary recalculations
          onRefresh: function (self) {
            if (self.progress > 0 || self.isActive) {
              if (!element.classList.contains("is-revealed")) {
                animateElement();
              }
            }
          },
          onEnter: () => {
            animateElement();
          },
        });
      }

      element.setAttribute("data-reveal-trigger-initialized", "true");
    });
  }

  window.initScrollRevealTrigger = () => {
    initElements();
    if (typeof ScrollTrigger !== "undefined") {
      ScrollTrigger.refresh();
    }
  };

  initElements();
}

function initImageFade() {
  if (typeof ScrollTrigger === "undefined") {
    console.warn(
      "ScrollTrigger is not available. data-image-fade will not work.",
    );
    return;
  }

  const fadeElements = document.querySelectorAll("[data-image-fade]");

  if (fadeElements.length === 0) {
    return;
  }

  fadeElements.forEach((element) => {
    if (element.hasAttribute("data-image-fade-initialized")) {
      return;
    }

    gsap.set(element, {
      opacity: 0.01,
      force3D: true, // Force GPU acceleration
    });

    const duration = element.dataset.imageFadeDuration
      ? parseFloat(element.dataset.imageFadeDuration)
      : 1;
    const delay = element.dataset.imageFadeDelay
      ? parseFloat(element.dataset.imageFadeDelay) * 1000
      : 0;
    const triggerStart = element.dataset.imageFadeStart || "top 75%";

    ScrollTrigger.create({
      trigger: element,
      start: triggerStart,
      once: true,
      onEnter: () => {
        gsap.to(element, {
          opacity: 1,
          duration: duration,
          delay: delay / 1000, // Convert to seconds
          ease: "power1.out", // Smooth easing for gentle transition
          force3D: true,
        });
      },
    });

    element.setAttribute("data-image-fade-initialized", "true");
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initImageFade);
} else {
  initImageFade();
}

window.initImageFade = initImageFade;

const initProjectSliderMob = () => {
  // Check if Slick is available
  if (typeof $.fn.slick === "undefined") {
    return;
  }

  const slider = $(".jsProjectSliderMob");
  const enableMobileSliderArrows =
    document.body.classList.contains("websites-page") ||
    document.body.classList.contains("webapps-page") ||
    document.body.classList.contains("mobileapps-page") ||
    document.body.classList.contains("ecommerce-page");

  if (slider.length === 0) {
    console.warn("Project slider element not found");
    return;
  }

  // More reliable check: both class and data
  const hasSlickClass = slider.hasClass("slick-initialized");
  const hasSlickData = slider.data("slick") !== undefined;
  const isInitialized = hasSlickClass || hasSlickData;

  if (!isInitialized) {
    try {
      // Slick deep-clones slides to build the infinite loop, inline styles and
      // all. The reveal system parks an element at opacity 0.01 until its own
      // ScrollTrigger fires, and a clone never gets a trigger of its own — so
      // any slide cloned while parked stays invisible for the life of the page.
      // That is what emptied the loop once it wrapped past the real slides.
      // Dropping the attribute on its own is too late: the parking has already
      // happened and the triggers already hold element references. Kill those
      // triggers and clear the parked properties, so what Slick copies is a
      // slide that is simply visible.
      const sliderSlides = slider.find(".projects-slider__slide");
      const sliderSlideElements = sliderSlides.toArray();

      sliderSlides
        .removeAttr("data-reveal-trigger")
        .removeAttr("data-reveal-trigger-initialized");

      if (typeof ScrollTrigger !== "undefined") {
        ScrollTrigger.getAll().forEach((instance) => {
          if (
            instance.trigger &&
            sliderSlideElements.indexOf(instance.trigger) > -1
          ) {
            instance.kill();
          }
        });
      }

      gsap.set(sliderSlideElements, {
        clearProps: "opacity,transform,visibility,willChange,transformOrigin",
      });

      // Double-check slider is still in DOM and valid
      if (slider.length === 0 || !document.body.contains(slider[0])) {
        return;
      }

      // Ease-in-out + single-slide steps hide Slick's infinite clone reset
      // (linear + slidesToScroll matching slidesToShow makes the loop jump obvious).
      const slideEase = "cubic-bezier(0.25, 0.1, 0.25, 1)";
      const slideSpeed = 650;

      slider.slick({
        dots: false,
        infinite: true,
        // The tiles carry a headline, a result and a paragraph of copy, so the
        // reader sets the pace: advancing under them mid-sentence is the one
        // thing that makes the section harder to read. Arrows, swipe and drag
        // still move it, and the loop is still infinite in both directions.
        autoplay: false,
        speed: slideSpeed,
        pauseOnHover: false,
        pauseOnFocus: false,
        slidesToShow: 3,
        slidesToScroll: 1,
        draggable: true,
        arrows: true,
        swipe: true,
        swipeToSlide: true,
        touchThreshold: 8,
        waitForAnimate: true,
        cssEase: slideEase,
        accessibility: true,
        prevArrow: `
          <button type="button" class="slick-prev" aria-label="Previous slide">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="19" viewBox="0 0 22 19" fill="none">
              <path d="M21.3335 8V10.6667H5.3335V13.3333H2.66683V10.6667H0.000164032V8H2.66683V5.33333H5.3335V8H21.3335ZM8.00016 2.66667H5.3335V5.33333H8.00016V2.66667ZM8.00016 2.66667H10.6668V0H8.00016V2.66667ZM8.00016 16H5.3335V13.3333H8.00016V16ZM8.00016 16H10.6668V18.6667H8.00016V16Z" fill="#FAFAF9"/>
            </svg>
          </button>
        `,
        nextArrow: `
          <button type="button" class="slick-next" aria-label="Next slide">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M5.3335 14.6667V17.3334H21.3335V20.0001H24.0002V17.3334H26.6668V14.6667H24.0002V12.0001H21.3335V14.6667H5.3335ZM18.6668 9.33341H21.3335V12.0001H18.6668V9.33341ZM18.6668 9.33341H16.0002V6.66675H18.6668V9.33341ZM18.6668 22.6667H21.3335V20.0001H18.6668V22.6667ZM18.6668 22.6667H16.0002V25.3334H18.6668V22.6667Z" fill="#FAFAF9"/>
            </svg>
          </button>
        `,
        responsive: [
          {
            breakpoint: 1200,
            settings: {
              slidesToShow: 3,
              slidesToScroll: 1,
              speed: slideSpeed,
              cssEase: slideEase,
            },
          },
          {
            breakpoint: 991,
            settings: {
              slidesToShow: 2,
              slidesToScroll: 1,
              swipeToSlide: true,
              infinite: true,
              autoplay: false,
              pauseOnHover: false,
              speed: slideSpeed,
              cssEase: slideEase,
            },
          },
          {
            breakpoint: 699,
            settings: {
              slidesToShow: enableMobileSliderArrows ? 1 : 1.1,
              slidesToScroll: 1,
              arrows: enableMobileSliderArrows,
              swipeToSlide: true,
              autoplay: false,
              autoplaySpeed: 0,
              speed: 400,
              infinite: false,
              cssEase: slideEase,
            },
          },
        ],
      });
    } catch (error) {
      console.error("Error initializing project slider:", error);
      // Clean up on error to prevent stuck state
      try {
        slider.removeClass("slick-initialized slick-slider");
        slider.removeData("slick");
        slider.find(".slick-track, .slick-list").remove();
      } catch {
        // Ignore cleanup errors
      }
    }
  }
};

window.initProjectSliderMob = initProjectSliderMob;

function initContactAccordion() {
  var $accordionTriggers = $(".accordion .accordion__title-wrapper");

  $accordionTriggers.each(function (index) {
    var $trigger = $(this);
    var contentId = $trigger.attr("aria-controls");
    var $content = $("#" + contentId);
    var $item = $trigger.closest(".accordion__item");

    if (index === 0) {
      $trigger.attr("aria-expanded", "true");
      $content.attr("aria-hidden", "false").show();
      $item.addClass("is-open");
    } else {
      $trigger.attr("aria-expanded", "false");
      $content.attr("aria-hidden", "true").hide();
      $item.removeClass("is-open");
    }
  });

  setTimeout(function () {
    if (typeof ScrollTrigger !== "undefined") {
      ScrollTrigger.refresh();
    }
  }, 100);

  $accordionTriggers.on("click", function () {
    var $this = $(this);
    var contentId = $this.attr("aria-controls");
    var isExpanded = $this.attr("aria-expanded") === "true";
    var $currentItem = $this.closest(".accordion__item");

    $accordionTriggers.each(function () {
      var $trigger = $(this);
      var $item = $trigger.closest(".accordion__item");
      var triggerContentId = $trigger.attr("aria-controls");
      var $triggerContent = $("#" + triggerContentId);

      $trigger.attr("aria-expanded", "false");
      $triggerContent.attr("aria-hidden", "true").slideUp();
      $item.removeClass("is-open");
    });

    if (!isExpanded) {
      $this.attr("aria-expanded", "true");
      $("#" + contentId)
        .attr("aria-hidden", "false")
        .slideDown(function () {
          if (typeof ScrollTrigger !== "undefined") {
            ScrollTrigger.refresh();
          }
        });
      $currentItem.addClass("is-open");
    } else {
      setTimeout(function () {
        if (typeof ScrollTrigger !== "undefined") {
          ScrollTrigger.refresh();
        }
      }, 400); // Wait for slideUp animation to complete
    }
  });

  $accordionTriggers.on("keydown", function (e) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      $(this).trigger("click");
    }
  });
}

function jsTab() {
  const impactTabElement = $(".jsTab");
  if (!impactTabElement.length) return;

  const tabButtons = $(".jsTabButton", impactTabElement);
  const tabContents = $(".jsTabContents > li.tab__item", impactTabElement);
  const loadMoreButton = $(".jsLoadMore", impactTabElement);
  const loadMoreWrapper = $(".loadmore-wrapper", impactTabElement);
  const noResultsMessage = $(".jsTabNoResults", impactTabElement);

  const initialCount = parseInt(
    impactTabElement.attr("data-load-more-initial") || "5",
    10,
  );
  const incrementCount = parseInt(
    impactTabElement.attr("data-load-more-increment") || "3",
    10,
  );

  let visibleCount = 0;
  let currentFilteredItems = $();

  const getActiveTargets = () =>
    tabButtons
      .filter(".is-active")
      .map(function () {
        return $(this).attr("data-tab-target").toLowerCase().trim();
      })
      .get();

  const updateLoadMoreButton = () => {
    if (currentFilteredItems.length === 0) {
      loadMoreWrapper.hide();
      loadMoreWrapper.removeAttr("data-reveal-trigger");
    } else {
      const shouldShow = currentFilteredItems.length > visibleCount;
      loadMoreWrapper.toggle(shouldShow);

      if (shouldShow && !loadMoreWrapper.attr("data-reveal-trigger")) {
        loadMoreWrapper.attr("data-reveal-trigger", "");
        if (typeof initScrollRevealTrigger === "function") {
          setTimeout(() => {
            initScrollRevealTrigger();
          }, 100);
        }
      } else if (!shouldShow) {
        loadMoreWrapper.removeAttr("data-reveal-trigger");
      }
    }
  };

  const applyLoadMoreVisibility = () => {
    tabContents.removeClass("is-visible");

    if (currentFilteredItems.length === 0) {
      const allBlogs = tabContents.filter(function () {
        const $item = $(this);
        const tabIds = $item.attr("data-tab-id");
        if (tabIds) {
          $item.addClass("is-active");
          return true;
        }
        return false;
      });
      allBlogs.addClass("is-visible");
    } else {
      currentFilteredItems.slice(0, visibleCount).addClass("is-visible");
    }
  };

  const showMoreItems = () => {
    visibleCount = Math.min(
      visibleCount + incrementCount,
      currentFilteredItems.length,
    );
    applyLoadMoreVisibility();
    updateLoadMoreButton();
  };

  const filterContent = () => {
    const activeTargets = getActiveTargets();

    currentFilteredItems = tabContents.filter(function () {
      const $item = $(this);
      const tabIds = $item.attr("data-tab-id");

      if (activeTargets.length === 0) {
        if (tabIds) {
          $item.addClass("is-active");
          return true;
        }
        $item.removeClass("is-active");
        return false;
      }

      if (!tabIds) {
        $item.removeClass("is-active");
        return false;
      }

      const itemTargets = tabIds
        .split(",")
        .map((target) => target.toLowerCase().trim());
      const hasMatch = itemTargets.some((itemTarget) =>
        activeTargets.includes(itemTarget),
      );

      $item.toggleClass("is-active", hasMatch);
      return hasMatch;
    });

    visibleCount = Math.min(initialCount, currentFilteredItems.length);
    applyLoadMoreVisibility();

    if (currentFilteredItems.length === 0) {
      noResultsMessage.addClass("is-visible");
    } else {
      noResultsMessage.removeClass("is-visible");
    }

    updateLoadMoreButton();
  };

  loadMoreButton.on("click", function (e) {
    e.preventDefault();
    showMoreItems();
  });

  const switchTabFromHash = () => {
    const hashSegment = window.location.hash.substring(1);
    const $tabButton = $(
      `[data-tab-target="${hashSegment}"]`,
      impactTabElement,
    );
    if ($tabButton.length) $tabButton.trigger("click");
  };

  tabButtons.on("click", function (e) {
    e.preventDefault();
    const $button = $(this);
    const buttonRect = this.getBoundingClientRect();
    const clickX = e.clientX - buttonRect.left;

    const closeIconArea = 24; // Icon (12px) + gap (8px) + some padding
    const isClickOnCloseIcon =
      $button.hasClass("is-active") && clickX < closeIconArea;

    if (isClickOnCloseIcon) {
      e.stopPropagation(); // Prevent any other handlers
      $button.removeClass("is-active");

      filterContent();
    } else {
      $button.toggleClass("is-active");
      filterContent();
    }
  });

  filterContent();
  switchTabFromHash();
  $(window).on("hashchange", switchTabFromHash);
}

/* eslint-disable */
function validator() {
  var $form = $(".application-form__form");

  // Custom validation methods
  $.validator.addMethod("letters", function (value, element) {
    return this.optional(element) || value == value.match(/^[a-zA-Z\s]*$/);
  });

  $.validator.addMethod("phone", function (phone_number, element) {
    phone_number = phone_number.replace(/\s+/g, "");
    return (
      this.optional(element) ||
      (phone_number.length > 9 && phone_number.match(/^[0-9()-]+$/))
    );
  });

  $.validator.addMethod("eitherEmailPhone", function (value, element) {
    var phone_number = value.replace(/\s+/g, "");
    var isPhone =
      this.optional(element) ||
      (phone_number.length > 9 && phone_number.match(/^[0-9()-]+$/));
    var isEmail =
      this.optional(element) ||
      /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i.test(
        value,
      );
    return isPhone || isEmail;
  });

  $form.each(function () {
    $(this).validate({
      rules: {
        name: {
          required: true,
          minlength: 3,
          letters: true,
        },
        first_name: {
          required: true,
          minlength: 3,
          letters: true,
        },
        email_from: {
          required: true,
          eitherEmailPhone: true,
        },
        phone: {
          phone: true,
        },
      },
      messages: {
        name: "Please specify your name",
        email_from: "Please specify a valid email address",
        phone: "Please specify a valid phone number",
        attachment: "Please attach your resume",
      },
      submitHandler: function (form, e) {
        e.preventDefault(); // Stop redirect
        var $form = $(form);
        var formData = new FormData($form[0]);
        var action = $form.attr("action");

        // Disable submit button
        $(".application-form__submit").attr("disabled", true);
        $(".form-message").addClass("active");
        // AJAX submission
        $.ajax({
          url: action,
          method: "POST",
          data: formData,
          processData: false,
          contentType: false,
          success: function (res) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: "job_application_submit",
              job_position: $form.find('input[name="position"]').val(),
              job_location: $form.find('input[name="location"]').val(),
              application_id: Date.now(), // anonymous unique identifier instead
            });
            // Show success message
            setTimeout(function () {
              var $successmsg = $(".success");

              setTimeout(function () {
                $(".circle-loader").addClass("removed");
                $successmsg.addClass("active");
              }, 2000);

              setTimeout(() => {
                $("#resume").val("");
                $(".application-form__attach-text").text("Attach Resume");
                form.reset();
                $(".application-form__submit").removeAttr("disabled");
                $(".form-message").removeClass("active");
                $(".circle-loader").removeClass("removed");
                $successmsg.removeClass("active");
              }, 6000);
            }, 3000);
          },
          error: function (err) {
            setTimeout(function () {
              var $errmsg = $(".error");

              setTimeout(function () {
                $(".circle-loader").addClass("removed");
                $errmsg.addClass("active");
              }, 2000);

              setTimeout(() => {
                $("#resume").val("");
                $(".application-form__attach-text").text("Attach Resume");
                form.reset();
                $(".application-form__submit").removeAttr("disabled");
                $(".form-message").removeClass("active");
                $(".circle-loader").removeClass("removed");
                $errmsg.removeClass("active");
              }, 6000);
            }, 3000);
          },
        });
      },
    });
  });

  // File input handling
  $("input[type=file]").on("change", function () {
    if (this.files.length === 0) {
      $(".file-dummy").addClass("invalid");
    } else {
      $(".file-dummy").removeClass("invalid");
    }
  });
}

function initHeaderDropdown() {
  const DESKTOP_MIN_WIDTH = 1280;
  const dropdowns = document.querySelectorAll(".header__dropdown");

  if (!dropdowns.length) return;

  function isMobileView() {
    return window.innerWidth < DESKTOP_MIN_WIDTH;
  }

  /* The submenu collapses to height 0 on small screens, so opening it means
     giving it a real number to animate to. Once it has finished opening the
     height is released to `auto`, otherwise a submenu that reflows — an
     orientation change, a font finally loading — would stay clipped. */
  function setExpanded(dropdown, isOpen) {
    const toggle = dropdown.querySelector(".header__dropdown-toggle");
    const menu = dropdown.querySelector(".header__dropdown-menu");

    dropdown.classList.toggle("is-open", isOpen);
    toggle?.setAttribute("aria-expanded", String(isOpen));

    if (!menu) return;

    if (!isMobileView()) {
      menu.style.height = "";
      return;
    }

    if (isOpen) {
      menu.style.height = `${menu.scrollHeight}px`;
      /* The submenu's own items animate too and their transitionend bubbles up
         here, so this has to wait for the height on the menu itself rather than
         taking the first event that arrives. */
      const onOpened = (event) => {
        if (event.target !== menu || event.propertyName !== "height") {
          return;
        }
        menu.removeEventListener("transitionend", onOpened);
        if (dropdown.classList.contains("is-open")) {
          menu.style.height = "auto";
        }
      };
      menu.addEventListener("transitionend", onOpened);
      return;
    }

    /* Going the other way needs a concrete starting height, since a transition
       out of `auto` does not run. The reflow between the two writes is forced
       synchronously rather than across a frame: rAF is throttled in a
       background tab, which would leave the submenu stuck open. */
    menu.style.height = `${menu.scrollHeight}px`;
    void menu.offsetHeight;
    menu.style.height = "0px";
  }

  function closeAllDropdowns() {
    dropdowns.forEach((dropdown) => setExpanded(dropdown, false));
  }

  dropdowns.forEach((dropdown) => {
    const toggle = dropdown.querySelector(".header__dropdown-toggle");
    if (!toggle) return;

    toggle.addEventListener("click", (event) => {
      if (!isMobileView()) return;

      event.preventDefault();

      const isOpen = dropdown.classList.contains("is-open");

      dropdowns.forEach((item) => {
        if (item !== dropdown) {
          setExpanded(item, false);
        }
      });

      setExpanded(dropdown, !isOpen);
    });

    toggle.addEventListener("keydown", (event) => {
      if (!isMobileView()) return;

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        toggle.click();
      }
    });
  });

  document.addEventListener("click", (event) => {
    if (!isMobileView()) return;

    const clickedInsideDropdown = [...dropdowns].some((dropdown) =>
      dropdown.contains(event.target),
    );

    if (!clickedInsideDropdown) {
      closeAllDropdowns();
    }
  });

  window.addEventListener("resize", () => {
    if (!isMobileView()) {
      closeAllDropdowns();
    }
  });
}

function setActiveHeaderNav() {
  const headerNavLinks = document.querySelectorAll(".header__nav a");
  const currentPath = window.location.pathname;

  headerNavLinks.forEach((link) => {
    const href = link.getAttribute("href");
    if (!href || href === "#") return;

    link.classList.remove("active");

    const normalizedHref = href === "/" ? "/" : href.replace(/^\/+/, "/");
    const normalizedPath =
      currentPath === "/" ? "/" : currentPath.replace(/^\/+/, "/");

    if (
      link.closest(".header__dropdown-menu") &&
      normalizedHref === "/" &&
      normalizedPath === "/"
    ) {
      return;
    }

    if (
      normalizedPath === normalizedHref ||
      normalizedPath === normalizedHref.replace(".html", "") ||
      (normalizedHref !== "/" &&
        normalizedPath.startsWith(normalizedHref.replace(".html", "")))
    ) {
      link.classList.add("active");
    }
  });
}

function addBlogCardRevealTrigger() {
  const blogCards = document.querySelectorAll(".wrg.blog-card");
  const isDesktop = window.innerWidth >= 1200;

  blogCards.forEach((card) => {
    if (isDesktop) {
      card.setAttribute("data-reveal-trigger", "");
    } else {
      card.removeAttribute("data-reveal-trigger");
    }
  });
}

/**
 * Initializes file upload feedback for the application form resume input.
 * Updates the attach label to "File uploaded successfully." when a file is selected.
 */
function resumeUploadFeedback() {
  const form = document.querySelector(
    "#application-form-join .application-form__form",
  );
  if (!form) return;

  const fileInput = form.querySelector("#resume");
  const attachText = form.querySelector(".application-form__attach-text");
  if (!fileInput || !attachText) return;

  const defaultLabel = "Attach Resume";
  const successLabel = "File uploaded successfully.";

  function updateLabel() {
    attachText.textContent =
      fileInput.files && fileInput.files.length > 0
        ? successLabel
        : defaultLabel;
  }

  fileInput.addEventListener("change", updateLabel);
}

/**
 * Cycles the footer's "Talk with an expert" headshot through the roster on
 * each page load. Persisted in localStorage so the rotation advances one
 * person per visit rather than repeating at random.
 */
function rotateFooterCtaPhoto() {
  var figure = document.querySelector(".js-footer--cta-figure");
  if (!figure) return;

  var img = figure.querySelector(".js-footer--cta-photo");
  if (!img) return;

  var people;
  try {
    people = JSON.parse(figure.getAttribute("data-cta-people") || "[]");
  } catch (e) {
    return;
  }
  if (!people.length) return;

  var storageKey = "wrgFooterCtaPhotoIndex";
  var index;
  try {
    var lastIndex = parseInt(window.localStorage.getItem(storageKey), 10);
    index = Number.isNaN(lastIndex) ? 0 : (lastIndex + 1) % people.length;
    window.localStorage.setItem(storageKey, String(index));
  } catch (e) {
    index = Math.floor(Math.random() * people.length);
  }

  var person = people[index];
  if (!person) return;

  img.src = person.src;
  img.alt = person.name;
}

/**
 * Wraps Termly's cookie accept button label in the shared .roll hover markup.
 * Scope AI reveal is independent (see home.js initScopeAiReveal).
 */
// function decorateCookieConsentButton() {
//   function decorateCookieButton() {
//     const btn = document.querySelector(".t-consentPrompt .t-acceptAllButton");
//     if (btn && !btn.querySelector(".roll")) {
//       btn.innerHTML =
//         '<span class="roll" data-label="Got it">' +
//         '<span class="roll__text">Got it</span>' +
//         "</span>";
//     }
//   }

//   decorateCookieButton();

//   const observer = new MutationObserver(decorateCookieButton);
//   observer.observe(document.body, { childList: true, subtree: true });
// }

document.addEventListener("DOMContentLoaded", function () {
  setActiveHeaderNav();
  initHeaderDropdown();
  addBlogCardRevealTrigger();
  initScrollReveal();
  initHeaderNavAnimation();
  resumeUploadFeedback();
  rotateFooterCtaPhoto();
  // decorateCookieConsentButton();
  validator();

  if (window.innerWidth >= 1199) {
    cursor.init();
  }
  setDefaultPointer();
  setupCardPrefetching();
  asciiHover();
  setTimeout(() => {
    initGlowingInteractiveDotsGrid();
  }, 100);
  // initGlassSurface();

  setTimeout(() => {
    initScrollRevealTrigger();
  }, 300);

  let cursorInitialized = window.innerWidth >= 1199;
  window.addEventListener("resize", function () {
    const isNormalDevice = window.innerWidth >= 1199;
    if (isNormalDevice && !cursorInitialized) {
      cursor.init();
      cursorInitialized = true;
    } else if (!isNormalDevice && cursorInitialized) {
      if (cursor.raf) {
        cancelAnimationFrame(cursor.raf);
        cursor.raf = null;
      }
      document.documentElement.style.setProperty("--cursor-opacity", "0");
      cursorInitialized = false;
    }

    addBlogCardRevealTrigger();
    if (isNormalDevice && typeof initScrollRevealTrigger === "function") {
      setTimeout(() => {
        initScrollRevealTrigger();
      }, 100);
    }
  });
});

$(window).on("load", function () {
  initProjectSliderMob();
  initContactAccordion();
  jsTab();
});

window.addEventListener("load", function () {
  window.animateHeaderNavWords = animateHeaderNavWords;
});

let resizeTimeout;
$(window).on("resize", function () {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    initProjectSliderMob();

    if (typeof window.initScrollReveal === "function") {
      window.initScrollReveal();
    }
    if (typeof window.initScrollRevealTrigger === "function") {
      window.initScrollRevealTrigger();
    }
  }, 150); // Debounce resize events
});

window.addEventListener("scroll", function () {
  if (typeof headerColorHandler === "function") {
    headerColorHandler();
  }
  peekingHeader();
});

/* Establish the header's state up front rather than waiting for the first
   scroll event, which on a clean load at the top never comes. */
peekingHeader();
